import InputField from 'components/styledFormElements/InputField'
import { ToggleField } from 'components/styledFormElements/ToggleField'
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline'
import { useFormContext } from 'react-hook-form'

type Platform = 'android' | 'ios'

const PLATFORM_CONFIG: Record<
    Platform,
    {
        label: string
        accentClass: string
        badgeClass: string
        iconClass: string
        description: string
    }
> = {
    android: {
        label: 'Android',
        accentClass: 'border-l-green-500',
        badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
        iconClass: 'text-green-600 dark:text-green-400',
        description: 'Configure minimum app version and force update behavior for Android users.',
    },
    ios: {
        label: 'iOS',
        accentClass: 'border-l-slate-700 dark:border-l-slate-300',
        badgeClass: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
        iconClass: 'text-slate-700 dark:text-slate-300',
        description: 'Configure minimum app version and force update behavior for iOS users.',
    },
}

const VERSION_PATTERN = /^\d+(\.\d+)*$/

type PlatformVersionConfigProps = {
    platform: Platform
}

const PlatformVersionConfig = ({ platform }: PlatformVersionConfigProps) => {
    const {
        register,
        formState: { errors },
    } = useFormContext<any>()

    const config = PLATFORM_CONFIG[platform]
    const fieldPrefix = `version_control.${platform}`
    const platformErrors = (errors.version_control as any)?.[platform]

    return (
        <div
            className={`p-4 border border-gray-200 dark:border-slate-700 rounded-lg border-l-4 ${config.accentClass} h-full flex flex-col`}
        >
            <div className="flex items-center gap-3 mb-4">
                <span
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${config.badgeClass}`}
                >
                    <DevicePhoneMobileIcon className={`h-4 w-4 ${config.iconClass}`} />
                    {config.label}
                </span>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {config.description}
            </p>

            <div className="space-y-4 flex-1">
                <InputField
                    label="Minimum Required Version"
                    type="text"
                    required
                    placeholder="e.g. 1.8.1"
                    helpText="Users below this version will be prompted to update the app."
                    register={register(`${fieldPrefix}.minimum_required_version`, {
                        required: `${config.label} minimum required version is required`,
                        pattern: {
                            value: VERSION_PATTERN,
                            message: 'Enter a valid version (e.g. 1.8.1)',
                        },
                    })}
                    error={platformErrors?.minimum_required_version?.message as string}
                />

                <InputField
                    label="Update Title"
                    type="text"
                    required
                    placeholder="e.g. Update Available"
                    helpText="Title shown in the force update dialog."
                    maxLength={100}
                    register={register(`${fieldPrefix}.title`, {
                        required: `${config.label} update title is required`,
                        maxLength: {
                            value: 100,
                            message: 'Title must be 100 characters or less',
                        },
                    })}
                    error={platformErrors?.title?.message as string}
                />

                <ToggleField
                    register={register(`${fieldPrefix}.force_update_enabled`)}
                    display="Enable/Disable"
                    label="Force Update"
                />
            </div>
        </div>
    )
}

const VersionControllerSettings = () => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            <PlatformVersionConfig platform="android" />
            <PlatformVersionConfig platform="ios" />
        </div>
    )
}

export default VersionControllerSettings
